import type { ButtonHTMLAttributes, MouseEvent, ReactNode, Ref } from 'react'
import { cx, isFilled } from '@/shared/lib'
import { Spinner } from '@/shared/ui/Spinner'
import s from './Button.module.css'

/**
 * Громкость действия: `primary` — заливка (одно главное действие на экран),
 * `tonal` — тинт («Добавить строку»), `secondary` — стекло (частые действия тулбара),
 * `ghost` — без фона (действие внутри строки таблицы).
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'tonal'

/** Ступени высоты, общие с Input: sm 32 — тулбар и панель выбора, md 38 — форма, lg 44 — CTA. */
export type ButtonSize = 'sm' | 'md' | 'lg'

/** Последствие действия: `danger` — необратимое («Удалить позицию», «Снять резерв»). */
export type ButtonTone = 'accent' | 'danger'

export type ButtonProps = {
  /** Подпись. Обязательна: кнопка без подписи — это IconButton, отдельная запись манифеста. */
  children: ReactNode
  variant?: ButtonVariant
  size?: ButtonSize
  tone?: ButtonTone
  /** Слот перед подписью: `plus` у «Добавить строку», `cart` у «В корзину». */
  iconLeft?: ReactNode
  /** Слот после подписи: шеврон у кнопки-триггера, счётчик выбранных строк. */
  iconRight?: ReactNode
  /** Идёт запрос: спиннер, `aria-busy`, действие погашено. Кнопку не выключает и фокус не теряет. */
  loading?: boolean
  /** Подпись на время запроса («Отправка в 1С…»). Резервирует ширину, чтобы кнопка не прыгала. */
  loadingLabel?: ReactNode
  /** Только вид ошибки: красная рамка и halo. Текст рисует FormField или потребитель. */
  invalid?: boolean
  /** Во всю ширину контейнера: submit в сайдбаре формы, CTA внизу корзины на мобильном. */
  block?: boolean
  ref?: Ref<HTMLButtonElement>
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'color'>

// CSS-модуль типизирован индексной сигнатурой, поэтому значения — string | undefined
const VARIANT_CLASS: Record<ButtonVariant, string | undefined> = {
  primary: s.primary,
  secondary: s.secondary,
  ghost: s.ghost,
  tonal: s.tonal,
}

const SIZE_CLASS: Record<ButtonSize, string | undefined> = {
  sm: s.sm,
  md: s.md,
  lg: s.lg,
}

const TONE_CLASS: Record<ButtonTone, string | undefined> = {
  accent: s.toneAccent,
  danger: s.toneDanger,
}

/**
 * Действие, у которого есть вес: отправить заявку в 1С, применить фильтр, снять резерв,
 * удалить позицию. Навигация сюда не входит — для перехода по адресу есть `Link`,
 * для действия без подписи — `IconButton`.
 *
 * Тег всегда `<button>`, `type` по умолчанию `"button"`: submit объявляется явно, иначе
 * «Добавить строку» отправляла бы форму заявки. Загрузка не выключает кнопку — фокус остаётся
 * на элементе, который пользователь только что нажал.
 *
 * Контракт: docs/ui-kit/contracts/button.md
 */
export function Button({
  children,
  variant = 'secondary',
  size = 'md',
  tone = 'accent',
  iconLeft,
  iconRight,
  loading = false,
  loadingLabel,
  invalid = false,
  block = false,
  type = 'button',
  disabled = false,
  className,
  onClick,
  ref,
  ...rest
}: ButtonProps) {
  // Пустым считается и boolean: идиома `can('edit') && t('…')` из правила 8 отдаёт false,
  // и слой загрузки без этой проверки рендерился бы пустым — кнопка теряла бы имя.
  const hasLoadingLabel = isFilled(loadingLabel)
  // Слой загрузки показывается только когда есть чем заменить подпись
  const loadingLayerShown = loading && hasLoadingLabel

  /*
   * Загрузка не ставит нативный disabled: он унёс бы фокус в body с элемента, который
   * пользователь только что нажал. Действие гасится двумя способами сразу — обработчик наружу
   * не идёт, а preventDefault отменяет действие по умолчанию: у type="submit" это единственный
   * способ не отправить форму второй раз.
   */
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (loading) {
      event.preventDefault()
      return
    }

    onClick?.(event)
  }

  /*
   * Спиннер декоративен: о загрузке сообщает сама кнопка через aria-busy.
   * tone="current" вместо ветвления по variant: подпись danger-кнопки в тёмной теме идёт
   * тушью --color-danger-text-on-solid, и белая дуга давала бы 1.7:1 рядом с ней.
   */
  const spinner = <Spinner size="xs" tone="current" />

  /*
   * Без подписи загрузки спиннер занимает место левой иконки — тогда ширина не меняется вовсе.
   * Если левой иконки нет, он добавляет свою колонку: это единственный случай, когда кнопка
   * всё-таки меняет ширину.
   */
  const lead = loading && !hasLoadingLabel ? spinner : iconLeft

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      className={cx(
        s.root,
        VARIANT_CLASS[variant],
        SIZE_CLASS[size],
        TONE_CLASS[tone],
        block && s.block,
        invalid && s.invalid,
        loading && s.loading,
        className,
      )}
      data-variant={variant}
      data-size={size}
      data-tone={tone}
      data-loading={loading ? 'true' : undefined}
      disabled={disabled}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      /*
       * aria-invalid не выставляется намеренно: роль button его не поддерживает — у кнопки нет
       * значения, которое может быть невалидным. Сообщение объявляет role="alert" снаружи.
       */
      onClick={handleClick}
    >
      {/* Оба состояния подписи лежат в одной ячейке грида: ширина — максимум из двух */}
      <span className={s.stack}>
        <span
          className={cx(s.layer, loadingLayerShown && s.layerHidden)}
          aria-hidden={loadingLayerShown || undefined}
        >
          {isFilled(lead) ? <span className={s.icon}>{lead}</span> : null}
          <span className={s.label}>{children}</span>
          {isFilled(iconRight) ? <span className={s.icon}>{iconRight}</span> : null}
        </span>

        {hasLoadingLabel ? (
          <span
            className={cx(s.layer, !loading && s.layerHidden)}
            aria-hidden={!loading || undefined}
          >
            <span className={s.icon}>{spinner}</span>
            <span className={s.label}>{loadingLabel}</span>
          </span>
        ) : null}
      </span>
    </button>
  )
}
